// Phase 43 Plan 06 — <CopilotSidebar /> (CANVAS-02).
//
// Right-column container that mounts the single unified CopilotFeed. Three
// layout modes:
//
//   1+2. Desktop (≥1280px): a SINGLE animated <aside> that fills the grid's
//      third track (`w-full`) and cross-fades between two always-mounted,
//      absolutely-positioned layers as the track animates 340px↔48px:
//        • Expanded (default): a 340px-pinned panel — a slim header bar with the
//          PanelRight collapse ICON button (same icon as the app shell,
//          header-logo.tsx:142) over the full-height CopilotFeed.
//        • Collapsed (user opt-in): a centered icon-rail — PanelRight expand
//          button + a vertical "AI Deckhand" identity (Sparkles +
//          [writing-mode:vertical-rl] text). Persisted via localStorage.
//      Phase 45.5.3 (smooth-collapse): the prior two-separate-<aside> swap +
//      `w-12`/`w-[340px]` width jump was replaced by this opacity cross-fade so
//      the rail TRAVELS with the column instead of snapping (the parent grid
//      owns the width transition; the container clips overflow during the
//      shrink).
//   3. Tablet/phone (<1280px): bottom-sheet FAB → Sheet body containing
//      the Feed (single tab) per UI-SPEC §"Layout Contract".
//
// Phase 45.5.3 (CANVAS-16): the collapse boolean is now CONTROLLABLE. When
// LiveFillCanvas passes `collapsed` + `onToggleCollapse` (the lifted state that
// also drives the grid's 340px→48px third track), this component is a pure
// presentation of that prop and the PARENT owns localStorage persistence. When
// the props are omitted (e.g. the existing prop-forwarding tests), it falls
// back to its own internal `collapsedUser` localStorage state so callers that
// don't lift the boolean keep working. Persistence happens in exactly ONE layer
// (gated on `collapsed === undefined`) so the key is never double-written.
//
// Phase 3B (Task 3.5 / Copilot Session): the separate ExtractionStatusPanel
// (live per-file reading) and CopilotActions (the "Fill missing specs" /
// "Regenerate description" CTAs) surfaces were REMOVED here (render + import
// only). Both are subsumed by the single CopilotFeed, which consumes
// useCopilotSession(tenantId, boatId) and renders the live rows, the logged
// narrative timeline, the terminal headline + Review CTA, and a disabled chat
// input in one thread. ExtractionStatusPanel.tsx + CopilotActions.tsx are
// preserved on disk (Phase 4 deletes them once no importer remains).

import { useEffect, useState } from 'react';
import { PanelRight, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@marine/ui/tooltip';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import { CopilotFeed } from './CopilotFeed';

const COLLAPSE_KEY = 'canvas-copilot-collapsed';

interface CopilotSidebarProps {
  tenantId: string | null;
  inventoryItemId: string | null;
  onJumpToField: (sectionId: string) => void;
  /** Fires when the dealer clicks the terminal "Review your listing" CTA. */
  onReviewExtraction?: () => void;
  /**
   * Phase 45.5.3 (CANVAS-16) — CONTROLLED collapse. When provided (LiveFillCanvas
   * lifts the boolean so the grid's third track shrinks 340px→48px in lock-step),
   * this component renders `collapsed` directly and the PARENT owns localStorage
   * persistence. When omitted, the component falls back to its internal
   * `collapsedUser` localStorage state (so prop-less callers/tests keep working).
   */
  collapsed?: boolean;
  /** Phase 45.5.3 (CANVAS-16) — fires on the PanelRight collapse/expand toggle (controlled path). */
  onToggleCollapse?: () => void;
}

export function CopilotSidebar({
  tenantId,
  inventoryItemId,
  onJumpToField,
  onReviewExtraction,
  collapsed,
  onToggleCollapse,
}: CopilotSidebarProps) {
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const [collapsedUser, setCollapsedUser] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  // Phase 45.5.3 (CANVAS-16) — prefer the controlled prop when LiveFillCanvas
  // lifts the boolean; otherwise fall back to the internal localStorage state.
  // Tablet/phone NEVER collapses regardless of which layer owns the value.
  const effectiveCollapsed = !isDesktop ? false : (collapsed ?? collapsedUser);
  const isControlled = collapsed !== undefined;

  // Single toggle handler. In the controlled path the parent owns the boolean
  // AND its persistence (Task 2 lifts both into LiveFillCanvasInner). In the
  // uncontrolled path we flip the internal state and the effect below persists.
  const handleToggle = () => {
    if (isControlled) {
      onToggleCollapse?.();
    } else {
      setCollapsedUser((c) => !c);
    }
  };

  useEffect(() => {
    // Persist ONLY when uncontrolled — when the parent controls `collapsed` it
    // owns the single localStorage write (no double-write to COLLAPSE_KEY).
    if (isControlled || !isDesktop) return;
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsedUser));
    } catch {
      /* localStorage may be unavailable in some test envs */
    }
  }, [collapsedUser, isDesktop, isControlled]);

  // ── Tablet/phone variant ──
  if (!isDesktop) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button
            variant="primary"
            size="sm"
            className="fixed bottom-4 end-4 rounded-full h-12 w-12 p-0 shadow-lg font-normal"
            aria-label="Open AI Deckhand"
          >
            <Sparkles className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[80vh]">
          <SheetTitle className="sr-only">AI Deckhand</SheetTitle>
          {/* Phase 3B (Task 3.5) — the single unified Copilot feed. The live
              per-file reading rows, the logged narrative timeline, the terminal
              headline + Review CTA, and the (disabled) chat input all live
              inside CopilotFeed now, driven by useCopilotSession(tenantId,
              boatId). The separate ExtractionStatusPanel + CopilotActions
              surfaces were retired. */}
          <CopilotFeed
            tenantId={tenantId}
            boatId={inventoryItemId}
            onJumpToField={onJumpToField}
            onReview={onReviewExtraction}
          />
        </SheetContent>
      </Sheet>
    );
  }

  // ── Desktop: ONE animated container, two cross-fading layers ──
  //
  // Phase 45.5.3 (Copilot smooth-collapse) — previously two SEPARATE <aside>
  // returns (a 48px rail and a 340px panel) swapped instantly, so the sidebar
  // snapped while the grid track jumped. Now a SINGLE <aside> fills the
  // grid's third track (`w-full`) and animates WITH it (the parent grid owns
  // the width transition; this container just tracks the track's width and
  // CLIPS overflow). Two absolutely-positioned layers — both ALWAYS mounted —
  // cross-fade their opacity so the content travels with the column:
  //
  //   • Expanded layer: pinned at w-[340px] so its internal layout does NOT
  //     reflow as the track shrinks; it clips under `overflow-hidden`, reading
  //     as a panel sliding out to the right. Fades to opacity-0 when collapsed.
  //   • Rail layer: the centered vertical identity. Fades to opacity-0 when
  //     expanded.
  //
  // Mount-safety (verified): the single CopilotFeed lives ONLY in the expanded
  // layer (never duplicated), and the mobile Sheet is a separate `!isDesktop`
  // return above — so there is exactly ONE CopilotFeed mounted and NO double WS
  // subscription. It stays mounted-while-collapsed (hidden via opacity, required
  // for the cross-fade); that is safe because the feed renders quiet (empty
  // state) until session activity arrives.
  return (
    <aside
      aria-label="AI Deckhand"
      className="relative w-full h-full border-s bg-card overflow-hidden"
    >
      {/* Expanded layer (340px, pinned width so it clips rather than reflows). */}
      <div
        className={cn(
          'absolute inset-0 flex w-[340px] flex-col transition-opacity duration-200 motion-reduce:transition-none',
          effectiveCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}
      >
        {/* The "AI Deckhand" identity now lives in this slim header bar, on the
            same row as the PanelRight collapse control (the feed below renders
            with showHeading={false} so the title isn't duplicated). */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-b p-2">
          <h2 className="ps-1 text-base font-semibold">AI Deckhand</h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="font-normal"
                onClick={handleToggle}
                aria-label="Collapse AI Deckhand"
                aria-expanded="true"
              >
                <PanelRight className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Collapse</TooltipContent>
          </Tooltip>
        </div>
        {/* Phase 3B (Task 3.5) — the single unified Copilot feed (see the tablet
            Sheet note). ExtractionStatusPanel + CopilotActions were retired.
            showHeading={false}: the title is rendered in the header bar above. */}
        <div className="min-h-0 flex-1">
          <CopilotFeed
            tenantId={tenantId}
            boatId={inventoryItemId}
            onJumpToField={onJumpToField}
            onReview={onReviewExtraction}
            showHeading={false}
          />
        </div>
      </div>

      {/* Rail layer (centered identity). Fades in when collapsed. */}
      <div
        className={cn(
          'absolute inset-0 flex flex-col items-center transition-opacity duration-200 motion-reduce:transition-none',
          !effectiveCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}
      >
        {/* PanelRight expand control on top — mirrors the app-shell header icon
            (header-logo.tsx:142). Shares the toggle with the vertical identity. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Open AI Deckhand"
              aria-expanded="false"
              onClick={handleToggle}
              className="w-12 h-12 mt-4 flex items-center justify-center"
            >
              <PanelRight className="h-4 w-4" role="img" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Open AI Deckhand</TooltipContent>
        </Tooltip>
        {/* Vertical "AI Deckhand" identity — Sparkles + sideways text. Also
            clickable to expand so the whole rail reads as the affordance. */}
        <button
          type="button"
          aria-label="Open AI Deckhand"
          onClick={handleToggle}
          className="mt-4 flex flex-col items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <Sparkles className="h-4 w-4" role="img" aria-hidden="true" />
          <span className="[writing-mode:vertical-rl] text-xs font-medium tracking-wide">
            AI Deckhand
          </span>
        </button>
      </div>
    </aside>
  );
}
